import React, { useState } from "react";
import {
	Box,
	Button,
	DrawerBody,
	DrawerFooter,
	DrawerHeader,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	IconButton,
	Input,
	ModalBody,
	ModalFooter,
	Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { v4 as uuidV4 } from "uuid";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours, qoriaColours } from "@/theme/colours";
import { BlockAllowToggle } from "./BlockAllowToggle";
import { RuleTargetPicker } from "./RuleTargetPicker";
import { RuleRow } from "./RuleRow";
import { RuleTargetType, ValidateRuleRequest } from "@/api/rulesLibrary";
import { ClasswizeModal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { MAX_LIST_NAME_LENGTH, MAX_RULES_PER_LIST } from "../constants";
import { scrollbarProps } from "../styles";
import { List, Rule, RuleAction } from "../types";
import {
	buildValidateRuleRequest,
	getRulesLibraryValidationCopy,
	hasListChanges,
	hasUnsavedListChanges,
	isLockedRuleConflictError,
	isRulesLibraryValidationError,
} from "../utils";
import { resolveRuleTarget, RulePickerTargetType } from "./utils";

interface Props {
	/** When provided, the form edits this list. When omitted, it creates a new one. */
	list?: List;
	onBack: () => void;
	onSave: (list: List) => void;
	validateRule: (body: ValidateRuleRequest) => Promise<void>;
	isSaving?: boolean;
}

const sectionLabelProps = {
	fontSize: "sm",
	fontWeight: 700,
	color: colours.text.regular,
} as const;

export function ListForm(props: Props): JSX.Element {
	const { t } = useTranslation();
	const { errorToast, warningToast } = useToast();
	const { list, onBack, onSave, validateRule, isSaving = false } = props;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const blockingRulesOnly = useClasswizeConfigStore((state) => state.data.create_blocking_rules_only) === true;

	const isEditing = list !== undefined;

	const [name, setName] = useState(list?.title ?? "");
	const [rules, setRules] = useState<Rule[]>(list?.rules ?? []);
	const [isValidating, setIsValidating] = useState(false);

	const [draftTarget, setDraftTarget] = useState("");
	const [draftTargetType, setDraftTargetType] = useState<RuleTargetType | undefined>();
	const [draftAction, setDraftAction] = useState<RuleAction>("block");
	const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

	const atRuleLimit = rules.length >= MAX_RULES_PER_LIST;
	const canAddRule = draftTarget.trim().length > 0 && !atRuleLimit && !isValidating;
	const canSave = name.trim().length > 0 && rules.length > 0 && (list === undefined || hasListChanges(list, { title: name, rules }));

	function validationCopyFor(error: unknown, candidate: Rule): { title: string; description: string; severity: "error" | "warning" } {
		if (isRulesLibraryValidationError(error)) {
			return getRulesLibraryValidationCopy({
				error,
				candidate,
				rules: [...rules.filter((rule) => rule.id !== candidate.id), candidate],
				t,
				lookupName,
			});
		}

		return {
			title: t("rulesLibrary.validation.cannotCreate"),
			description: t("errors.unexpectedError"),
			severity: "error",
		};
	}

	async function requestValidation(candidate: Rule): Promise<{ ok: true } | { ok: false; error: unknown }> {
		setIsValidating(true);
		try {
			await validateRule(buildValidateRuleRequest(candidate, rules));
			return { ok: true };
		} catch (error) {
			return { ok: false, error };
		} finally {
			setIsValidating(false);
		}
	}

	/** Shows the validation toast and returns whether the candidate can still be kept. */
	function handleValidationError(error: unknown, candidate: Rule): boolean {
		const copy = validationCopyFor(error, candidate);
		if (isLockedRuleConflictError(error)) {
			warningToast({
				title: copy.title,
				description: copy.description,
				isClosable: true,
			});
			return true;
		}
		errorToast({
			title: copy.title,
			description: copy.description,
			isClosable: true,
		});
		return false;
	}

	async function validateCandidate(candidate: Rule): Promise<boolean> {
		const result = await requestValidation(candidate);
		return result.ok || handleValidationError(result.error, candidate);
	}

	/** The rule in this list with the same target but the opposite action, if that is why validation failed. */
	function findDirectConflictRule(error: unknown): Rule | undefined {
		if (!isRulesLibraryValidationError(error)) return undefined;
		const [firstError] = error.errors;
		if (firstError?.code !== "CONFLICT" || !firstError.relatedRuleId) return undefined;
		return rules.find((rule) => rule.id === firstError.relatedRuleId);
	}

	async function updateRuleAction(current: Rule, action: RuleAction): Promise<boolean> {
		const candidate = { ...current, action };
		const isValid = await validateCandidate(candidate);
		if (isValid) {
			setRules((prev) => prev.map((rule) => (rule.id === current.id ? candidate : rule)));
		}
		return isValid;
	}

	function handleDraftTargetChange(value: string, optionType?: RulePickerTargetType): void {
		setDraftTarget(value);
		setDraftTargetType(value === "" || optionType === "APP_CONTROL" ? undefined : optionType);
	}

	async function handleAddRule(): Promise<void> {
		if (!canAddRule) return;
		const resolved = draftTargetType === "SIGNATURE" ? { target: draftTarget, type: "SIGNATURE" as const } : resolveRuleTarget(draftTarget);
		const candidate: Rule = {
			id: uuidV4(),
			target: resolved.target,
			action: blockingRulesOnly ? "block" : draftAction,
			type: resolved.type,
		};

		const result = await requestValidation(candidate);
		if (!result.ok) {
			const conflictingRule = findDirectConflictRule(result.error);
			if (conflictingRule) {
				if (await updateRuleAction(conflictingRule, candidate.action)) {
					clearDraft();
				}
				return;
			}
			if (!handleValidationError(result.error, candidate)) {
				return;
			}
		}

		setRules((prev) => [candidate, ...prev]);
		clearDraft();
	}

	function clearDraft(): void {
		setDraftTarget("");
		setDraftTargetType(undefined);
	}

	async function handleChangeRuleAction(id: string, action: RuleAction): Promise<void> {
		if (isValidating || blockingRulesOnly) {
			return;
		}

		const current = rules.find((rule) => rule.id === id);
		if (!current || current.action === action) {
			return;
		}

		await updateRuleAction(current, action);
	}

	function handleDeleteRule(id: string) {
		setRules((prev) => prev.filter((rule) => rule.id !== id));
	}

	function handleSave() {
		if (!canSave) return;
		onSave({ id: list?.id ?? uuidV4(), title: name.trim(), rules });
	}

	function handleBack() {
		if (hasUnsavedListChanges(list, { title: name, rules })) {
			setIsLeaveConfirmOpen(true);
			return;
		}

		onBack();
	}

	function handleConfirmLeave() {
		setIsLeaveConfirmOpen(false);
		onBack();
	}

	return (
		<>
			<DrawerHeader borderBottom="1px solid" borderColor={qoriaColours.brand.neutral[100]} px={6} py={4}>
				<Flex align="center" gap={3}>
					<IconButton
						aria-label={t("rulesLibrary.form.back")}
						variant="ghost"
						onClick={handleBack}
						icon={<ChakraFAIcon icon="fa-arrow-left" variant="solid" color={colours.text.regular} boxSize={4} />}
					/>
					<Text fontSize="xl" fontWeight="medium" color={colours.text.title}>
						{isEditing ? t("rulesLibrary.form.editListTitle") : t("rulesLibrary.form.newListTitle")}
					</Text>
				</Flex>
			</DrawerHeader>

			<DrawerBody px={6} py={5} sx={scrollbarProps}>
				<FormControl mb={6}>
					<Flex justify="space-between" mb={2}>
						<FormLabel {...sectionLabelProps} mb={0} me={0}>
							{t("rulesLibrary.form.nameLabel")}
						</FormLabel>
						<FormHelperText mt={0} fontSize="sm" color={colours.text.regular}>
							{t("rulesLibrary.form.nameMaxChars", { max: MAX_LIST_NAME_LENGTH })}
						</FormHelperText>
					</Flex>
					<Input
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder={t("rulesLibrary.form.namePlaceholder")}
						maxLength={MAX_LIST_NAME_LENGTH}
					/>
				</FormControl>

				<Text fontSize="xl" fontWeight="medium" color={colours.text.title} mb={3}>
					{t("rulesLibrary.form.rulesHeading")}
				</Text>

				<Box border="1px solid" borderColor={qoriaColours.brand.neutral[100]} rounded="xl" p={4} mb={4}>
					<Text {...sectionLabelProps} mb={3}>
						{t("rulesLibrary.form.addItems")}
					</Text>
					<Box mb={3}>
						<RuleTargetPicker
							value={draftTarget}
							onChange={handleDraftTargetChange}
							onEnter={() => {
								void handleAddRule();
							}}
							isDisabled={atRuleLimit}
						/>
					</Box>
					<Box mb={3}>
						<BlockAllowToggle value={draftAction} onChange={setDraftAction} />
					</Box>
					<Button
						width="full"
						onClick={() => {
							void handleAddRule();
						}}
						isDisabled={!canAddRule}
						isLoading={isValidating}
						leftIcon={<ChakraFAIcon icon="fa-plus" variant="solid" boxSize={3.5} />}
					>
						{t("rulesLibrary.form.addRule")}
					</Button>
				</Box>

				<Box border="1px solid" borderColor={qoriaColours.brand.neutral[100]} rounded="xl" overflow="hidden">
					<Flex
						justify="space-between"
						align="center"
						bg={qoriaColours.brand.neutral[50]}
						borderBottom="1px solid"
						borderColor={qoriaColours.brand.neutral[100]}
						px={4}
						py={3}
					>
						<Text {...sectionLabelProps}>{t("rulesLibrary.form.rulesInList")}</Text>
						<Text fontSize="sm" color={colours.text.regular}>
							{t("rulesLibrary.form.rulesCounter", { count: rules.length, max: MAX_RULES_PER_LIST })}
						</Text>
					</Flex>

					{rules.length === 0 ? (
						<Text fontSize="sm" fontStyle="italic" color={colours.text.regular} px={4} py={5}>
							{t("rulesLibrary.form.emptyRules")}
						</Text>
					) : (
						rules.map((rule, index) => (
							<RuleRow
								key={rule.id}
								rule={rule}
								isFirst={index === 0}
								onChangeAction={(action) => {
									void handleChangeRuleAction(rule.id, action);
								}}
								onDelete={() => handleDeleteRule(rule.id)}
							/>
						))
					)}
				</Box>
			</DrawerBody>

			<DrawerFooter borderTop="1px solid" borderColor={qoriaColours.brand.neutral[100]}>
				<Button
					onClick={handleSave}
					isDisabled={!canSave || isSaving}
					isLoading={isSaving}
					leftIcon={<ChakraFAIcon icon="fa-floppy-disk" variant="regular" boxSize={4} />}
				>
					{t("rulesLibrary.form.save")}
				</Button>
			</DrawerFooter>

			<ClasswizeModal
				isOpen={isLeaveConfirmOpen}
				onClose={() => setIsLeaveConfirmOpen(false)}
				title={t("rulesLibrary.form.unsavedChangesTitle")}
				size="lg"
				dataTestId="rules-library-unsaved-changes"
			>
				<ModalBody>
					<Text>{t("rulesLibrary.form.unsavedChangesBody")}</Text>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button variant="ghost" onClick={() => setIsLeaveConfirmOpen(false)}>
						{t("rulesLibrary.form.unsavedChangesStay")}
					</Button>
					<Button onClick={handleConfirmLeave}>{t("rulesLibrary.form.unsavedChangesLeave")}</Button>
				</ModalFooter>
			</ClasswizeModal>
		</>
	);
}
